import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import { comics, getComic } from "@/mocks/comics";
import { getGenre } from "@/mocks/genres";

const chapterTitles = [
  "初见", "重逢", "秘密", "约定", "误会", "心迹", "风暴", "转折",
  "告白", "真相", "抉择", "和解", "前路", "新章", "终章",
];

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComic(id!);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!comic) return;
    const list: string[] = JSON.parse(localStorage.getItem("hanman-bookshelf") ?? "[]");
    setSaved(list.includes(comic.id));
  }, [comic]);

  const chapters = useMemo(() => {
    if (!comic) return [];
    return Array.from({ length: Math.min(comic.chapters, 24) }, (_, i) => {
      const num = comic.chapters - i;
      return {
        num,
        title: `${chapterTitles[(comic.chapters - i) % chapterTitles.length]} · 第${num}话`,
        isNew: i === 0,
      };
    });
  }, [comic]);

  if (!comic) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-32 text-center">
          <p className="text-lg font-semibold text-foreground-950">未找到这部韩漫</p>
          <Link to="/" className="btn-primary px-5 py-2 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const genre = getGenre(comic.genre);
  const related = comics.filter((c) => c.genre === comic.genre && c.id !== comic.id).slice(0, 5);

  const toggleSave = () => {
    const list: string[] = JSON.parse(localStorage.getItem("hanman-bookshelf") ?? "[]");
    const next = saved ? list.filter((x) => x !== comic.id) : [...list, comic.id];
    localStorage.setItem("hanman-bookshelf", JSON.stringify(next));
    setSaved(!saved);
    setToast(saved ? "已取消收藏" : "已加入书架，可在追漫列表查看");
    setTimeout(() => setToast(null), 2200);
  };

  const startReading = () => {
    document.getElementById("chapters")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: `${genre.name}漫`, to: `/category/${comic.genre}` },
            { label: comic.title },
          ]}
        />

        <div className="container-x mt-4">
          <section className="flex flex-col gap-6 md:flex-row">
            <div className="w-full shrink-0 sm:w-56 md:w-64">
              <div className="aspect-[2/3] overflow-hidden rounded-lg border border-background-200">
                <img
                  src={comic.cover}
                  alt={`${comic.title}韩漫封面`}
                  title={`${comic.title} · ${genre.name}漫画`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {comic.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                  {genre.name}题材
                </span>
                <span className={comic.status === "连载中" ? "text-accent-700" : "text-foreground-500"}>
                  <i className="mr-1 ri-radio-button-line" />
                  {comic.status}
                </span>
                <span><i className="mr-1 ri-user-line" />{comic.author}</span>
                <span className="flex items-center gap-1 text-secondary-700">
                  <i className="ri-star-fill" /> {comic.rating.toFixed(1)}
                </span>
                <span><i className="mr-1 ri-eye-line" />{comic.views}万阅读</span>
                <span>{comic.chapters}话</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-700"
                  >
                    #{t}
                  </span>
                ))}
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {comic.desc}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <button type="button" onClick={startReading} className="btn-primary h-11 px-6 text-sm">
                  <i className="ri-play-circle-line text-lg" /> 开始阅读
                </button>
                <button
                  type="button"
                  onClick={toggleSave}
                  className={`btn h-11 px-6 text-sm ${
                    saved
                      ? "bg-secondary-500 text-background-50 hover:bg-secondary-600"
                      : "bg-background-100 text-foreground-800 hover:bg-background-200"
                  }`}
                >
                  <i className={`${saved ? "ri-bookmark-fill" : "ri-bookmark-line"} text-lg`} />
                  {saved ? "已在书架" : "加入书架"}
                </button>
              </div>

              <p className="mt-4 text-xs text-foreground-500">
                <i className="ri-time-line mr-1" />
                {comic.updateTime} · 完结后可一次看完全部章节
              </p>
            </div>
          </section>
        </div>

        <section id="chapters" className="container-x py-10 md:py-12">
          <h2 className="mb-1 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-list-check-2 text-primary-500" />
            章节目录
          </h2>
          <p className="mb-4 text-sm text-foreground-600">
            《{comic.title}》共 {comic.chapters} 话 · 以下展示最近更新
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((ch) => (
              <button
                key={ch.num}
                type="button"
                onClick={startReading}
                className="group flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm transition-colors hover:border-primary-300 hover:bg-primary-50/40"
              >
                <span className="flex items-center gap-2 text-foreground-700 group-hover:text-primary-700">
                  <i className="ri-file-text-line text-foreground-400" />
                  {ch.title}
                </span>
                {ch.isNew && (
                  <span className="rounded bg-primary-500 px-1.5 py-0.5 text-[10px] font-semibold text-background-50">
                    NEW
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        <section className="section-pad bg-background-100">
          <div className="container-x">
            <h2 className="mb-1 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-thumb-up-line text-primary-500" />
              同题材推荐
            </h2>
            <p className="mb-5 text-sm text-foreground-600">还在{genre.name}题材中挑选？这些人气韩漫也值得一看。</p>
            {related.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
                {related.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            ) : (
              <Link to={`/category/${comic.genre}`} className="btn-ghost px-4 py-2 text-sm">
                去{genre.name}分类查看更多
              </Link>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50 shadow-soft">
          <i className={`mr-1 ${saved ? "ri-check-line" : "ri-error-warning-line"}`} />
          {toast}
        </div>
      )}
    </div>
  );
}